import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Modal, Form, Input, Select, Button, message, Spin } from "antd";

import { MailOutlined, BankOutlined } from "@ant-design/icons";

import http from "@/utils/http";
import api from "@/utils/api";

const Edit = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [initLoading, setInitLoading] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [trustId, setTrustId] = useState(null);

  function useLazySelect(apiUrl, mapper, enableCache = true) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(null);

    const load = async (forceReload = false) => {
      if (options.length && enableCache && !forceReload) return options;
      if (loadingRef.current && !forceReload) return loadingRef.current;

      const request = (async () => {
        setLoading(true);
        try {
          const res = await http.get(apiUrl);
          const rawData = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.owner)
              ? res.data.owner
              : res?.data || [];
          const nextOptions = mapper(rawData);
          setOptions(nextOptions);
          return nextOptions;
        } catch (err) {
          console.error("Select load error:", err);
          setOptions([]);
          return [];
        } finally {
          setLoading(false);
          loadingRef.current = null;
        }
      })();

      loadingRef.current = request;
      return request;
    };

    return { options, loading, load };
  }

  /* ---------------------- Select Data ---------------------- */
  // const stateSelect = useLazySelect(api.getStateDictList, (data) =>
  //   data.map((s) => ({ label: s, value: s })),
  // );
const stateSelect = useLazySelect(api.getStateDictList, (data) =>
  Object.entries(data || {}).map(([key, label]) => ({
    label,
    value: key,
  })),
);

  const ownerSelect = useLazySelect(api.getOwnerDictList, (data) =>
    data.map((d) => ({
      label: d.titleName,
      value: d.ownerId,
    })),
  );

  const glChartNameOptions = [
    { label: "Z64", value: 1 },
    { label: "GEN", value: 2 },
    { label: "STORE", value: 3 },
  ];

  const serviceNameOptions = [
    { label: "Z64", value: 1 },
    { label: "GEN", value: 2 },
  ];

  /* ----------------------  详情加载 ---------------------- */
  const loadDetail = async (id) => {
    try {
      setInitLoading(true);

      const res = await http.get(api.getTrustByIdData, {
        params: { trustId: id },
      });
      const detail = res.data || {};
      const ownerOptions = await ownerSelect.load();
      const matchedOwner = ownerOptions.find(
        (item) => String(item?.value) === String(detail?.ownerId),
      );

      setTrustId(detail.trustId);

      const bankList = Array.isArray(detail.bankList) ? detail.bankList : [];

      form.setFieldsValue({
        ...detail,
        bankList,
        glChartNameId: detail.glChartNameId
          ? Number(detail.glChartNameId)
          : undefined,

        serviceNameId: detail.serviceNameId
          ? Number(detail.serviceNameId)
          : undefined,

        ownerId: matchedOwner?.value ?? detail.ownerId ?? undefined,
        active:
          detail.active !== undefined && detail.active !== null
            ? String(detail.active)
            : undefined,
      });
    } catch (err) {
      console.error(err);
      message.error("Failed to load trust detail");
    } finally {
      setInitLoading(false);
    }
  };


  useImperativeHandle(ref, () => ({
    open: async (record) => {
      form.resetFields();
      setVisible(true);
      if (record?.trustId) {
        await loadDetail(record.trustId);
      }
    },

    setModelAdd: async () => {
      form.resetFields();
      setVisible(true);
      await ownerSelect.load();
      form.setFieldsValue({ bankList: [{}] });
    },
  }));


  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        trustId: trustId,
        ...values,
      };

      const res = await http.post(api.getTrustEdit, payload);

      if (res?.code === "1") {
        message.success("Trust updated successfully");
        setVisible(false);
        form.resetFields();
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg || "Update failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "red" }}>
          <MailOutlined style={{ marginRight: "8px" }} />
          EDIT TRUST
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      width="35%"
      centered
      open={visible}
      maskClosable={false}
      onCancel={() => {
        setVisible(false);
        form.resetFields();
      }}
      footer={
           <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
        <Button
          key="close"
          onClick={() => {
            setVisible(false);
            form.resetFields();
          }}
        >
          Close
        </Button>
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          Submit
        </Button>
      </div>}
    >
      <Spin spinning={initLoading}>
        <Form
          form={form}
          layout="horizontal"
          onFinish={onFinish}
          labelCol={{ style: { width: "180px" } }}
          className="compact-form"   
        >
          <div style={{ width: "100%" }}>
            <div className="modal-section-title">
              <BankOutlined /> Trust Detail
            </div>

            <Form.Item name="trust" label="Trust Code" rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>

            <Form.Item
              name="trustName"
              label="Trust Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="ownerId"
              label="Owner"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select Owner"
                showSearch
                allowClear
                options={ownerSelect.options}
                loading={ownerSelect.loading}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const keyword = input.toLowerCase();
                  const label = option?.label?.toString().toLowerCase() || "";
                  const value = option?.value?.toString().toLowerCase() || "";

                  return label.includes(keyword) || value.includes(keyword);
                }}
              />
            </Form.Item>

            <Form.Item
              name="glChartNameId"
              label="GL Chart Name"
              rules={[{ required: true }]}
            >
              <Select options={glChartNameOptions} />
            </Form.Item>

            <Form.Item
              name="serviceNameId"
              label="Service Name"
              rules={[{ required: true }]}
            >
              <Select options={serviceNameOptions} />
            </Form.Item>

            <Form.Item name="street" label="Street">
              <Input />
            </Form.Item>

            <Form.Item name="city" label="City">
              <Input />
            </Form.Item>

            <Form.Item name="county" label="County">
              <Input />
            </Form.Item>

            <Form.Item name="state" label="State" >
            <Select
  placeholder="Select State"
  options={stateSelect.options}
  loading={stateSelect.loading}
  showSearch
  optionFilterProp="label"
  onDropdownVisibleChange={(open) => open && stateSelect.load()}
/>
            </Form.Item>

            <Form.Item name="zip" label="Zip" >
              <Input />
            </Form.Item>

            <Form.Item name="fedId" label="Federal Tax ID">
              <Input />
            </Form.Item>

            <Form.Item name="stateId" label="State Tax ID">
              <Input />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item
              name="active"
              label="Active"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ]}
              />
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
});

export default Edit;
